import { SKILLS, KEYSTONES, ITEMS, CONSUMABLES, HEROES, BLESSINGS, CROWD_EFFECTS, aspectOf, aspectPath, resolveSkill, skillNumbers, type HeroId, type ItemInst } from '@knight/shared';
import type { ComponentChildren } from 'preact';
import { signal } from '@preact/signals';
import { t, tn, td } from '../i18n';
import { heroPortrait } from '../game/art';
import { iconUrl } from '../game/icons';
import { useEffect, useRef } from 'preact/hooks';
import { isTouchUi } from '../app/device';

// ------------------------------------------------------------------ tooltip

export interface TipContent {
  title: string;
  sub?: string;
  body?: string;
  lines?: string[];
  tags?: string[];
  color?: string;
}

export const tooltip = signal<(TipContent & { x: number; y: number; below: boolean }) | null>(null);

let tipOwner: HTMLElement | null = null;

export function showTip(el: HTMLElement, tip: TipContent): void {
  const r = el.getBoundingClientRect();
  const below = r.top < 190;
  tipOwner = el;
  tooltip.value = { ...tip, x: r.left + r.width / 2, y: below ? r.bottom + 10 : r.top - 10, below };
}

export function hideTip(): void {
  tipOwner = null;
  if (tooltip.value) tooltip.value = null;
}

/**
 * Fires on pointerdown so a tap answers at once, and swallows the click the browser sends after
 * a touch so nothing happens twice.
 */
export function press(fn: () => void): { onPointerDown: (e: PointerEvent) => void; onClick: (e: MouseEvent) => void } {
  let fired = 0;
  return {
    onPointerDown: (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      e.preventDefault();
      e.stopPropagation();
      fired = performance.now();
      fn();
    },
    onClick: (e: MouseEvent) => {
      e.stopPropagation();
      if (performance.now() - fired < 700) return;
      fn();
    },
  };
}

/** Hides the tooltip only if this element owns it (used when the element unmounts). */
export function hideTipOf(el: HTMLElement | null): void {
  if (el && tipOwner === el) hideTip();
}

export function tipOwnedBy(el: HTMLElement | null): boolean {
  return !!el && tipOwner === el;
}

export function Tooltip() {
  const tp = tooltip.value;
  if (!tp) return null;
  const left = Math.max(130, Math.min(window.innerWidth - 130, tp.x));
  return (
    <div class={'tooltip' + (tp.below ? ' below' : '')} style={{ left, top: tp.y }}>
      <div class="tt-title" style={{ color: tp.color ?? 'var(--gold)' }}>
        {tp.title}
      </div>
      {tp.sub && <div class="tt-sub">{tp.sub}</div>}
      {tp.body && <div class="tt-body">{tp.body}</div>}
      {tp.lines?.map((l) => (
        <div class="tt-line">{l}</div>
      ))}
      {tp.tags && tp.tags.length > 0 && (
        <div class="tt-tags">
          {tp.tags.map((tg) => (
            <span class="tag">{tg}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Skill text with {dur} filled in from the numbers this rank and aspect path actually have. */
export function skillDesc(id: string, level?: number, aspects?: string[]): string {
  const def = SKILLS[id];
  const n = def ? skillNumbers(def, aspects, level ?? 1) : undefined;
  return td('skill', id).replace('{dur}', String(n?.duration ?? 14));
}

/** What the skill is worth right now: cooldown, count, radius, duration, damage from its rank. */
export function numberLines(id: string, aspects: string[] | undefined, level: number): string[] {
  const def = SKILLS[id];
  if (!def || def.kind === 'trait') return [];
  const n = skillNumbers(def, aspects, level);
  const out: string[] = [];
  if (n.cooldown > 0) out.push(`${t('stat.cooldown')}: ${n.cooldown} ${t('label.secShort')}`);
  if (n.count !== undefined) out.push(`${t('gain.count')}: ${n.count}`);
  if (n.radius !== undefined) out.push(`${t('gain.radius')}: ${n.radius}`);
  if (n.duration !== undefined) out.push(`${t('gain.duration')}: ${n.duration} ${t('label.secShort')}`);
  if (n.damage !== 1) out.push(`${t('gain.damage')}: +${Math.round((n.damage - 1) * 100)}%`);
  return out;
}

/** What one more rank changes, so an upgrade card is never a mystery. */
export function gainLines(id: string, aspects: string[] | undefined, from: number, to: number): string[] {
  const def = SKILLS[id];
  if (!def) return [];
  if (def.kind === 'trait') return (def.modsPerLevel ?? []).map((m) => affixText(m.stat, m.add, m.inc));
  const a = skillNumbers(def, aspects, from);
  const b = skillNumbers(def, aspects, to);
  const out: string[] = [];
  if (b.damage !== a.damage) out.push(`${t('gain.damage')} +${Math.round((b.damage - a.damage) * 100)}%`);
  if (a.count !== undefined && b.count !== undefined && b.count !== a.count) out.push(`${t('gain.count')} ${a.count} → ${b.count}`);
  if (a.radius !== undefined && b.radius !== undefined && b.radius !== a.radius) out.push(`${t('gain.radius')} ${a.radius} → ${b.radius}`);
  if (a.duration !== undefined && b.duration !== undefined && b.duration !== a.duration) out.push(`${t('gain.duration')} ${a.duration} → ${b.duration} ${t('label.secShort')}`);
  if (b.cooldown !== a.cooldown) out.push(`${t('stat.cooldown')} ${a.cooldown} → ${b.cooldown} ${t('label.secShort')}`);
  return out;
}

export function skillTip(id: string, level?: number, aspects?: string[] | string): TipContent {
  const def = SKILLS[id];
  const path = typeof aspects === 'string' ? aspectPath(id, [aspects]) : aspectPath(id, aspects ?? []);
  const ids = path.map((a) => a.id);
  const res = def ? resolveSkill(def, ids) : undefined;
  const kind = def?.kind === 'active' ? t('label.actives') : def?.kind === 'auto' ? t('label.autos') : t('label.traits');
  const lines = numberLines(id, ids, level ?? 1);
  for (const a of path) lines.push(`◆ ${tn('aspect', a.id)}: ${td('aspect', a.id)}`);
  if (def && def.kind !== 'trait' && level !== undefined && level < def.maxLevel) {
    const next = gainLines(id, ids, level, level + 1);
    if (next.length) lines.push(`${t('label.levelGain', { n: level + 1 })}: ${next.join(', ')}`);
  }
  return {
    title: tn('skill', id) + (level ? ` · ${t('label.lvl')} ${level}` : ''),
    sub: kind + (path.length ? ` · ${path.map((a) => tn('aspect', a.id)).join(' → ')}` : ''),
    body: skillDesc(id, level, ids),
    lines,
    tags: def?.tags ?? [],
    color: res?.color ?? def?.color,
  };
}

export function aspectTip(skillId: string, aspectId: string, ring: 1 | 2, cost?: number): TipContent {
  const asp = aspectOf(skillId, aspectId);
  const lines: string[] = [];
  if (cost !== undefined) lines.push(t('label.costPoints', { n: cost }));
  if (asp?.cooldownMul && asp.cooldownMul !== 1) lines.push(`${t('stat.cooldown')} ×${asp.cooldownMul}`);
  return { title: tn('aspect', aspectId), sub: `${t(ring === 1 ? 'label.ring1' : 'label.ring2')} · ${tn('skill', skillId)}`, body: td('aspect', aspectId), lines, color: asp?.color ?? SKILLS[skillId]?.color };
}

export function blessingTip(id: string, stacks?: number): TipContent {
  const def = BLESSINGS[id];
  return { title: tn('blessing', id) + (stacks && stacks > 1 ? ` ×${stacks}` : ''), sub: t('label.blessing') + (def ? ` · ${t('label.rarity' + def.rarity)}` : ''), body: td('blessing', id), tags: def?.tags ?? [], color: def?.color };
}

export function crowdTip(id: string, price?: number): TipContent {
  const def = CROWD_EFFECTS[id];
  const lines: string[] = [];
  if (price !== undefined) lines.push(t('label.priceFame', { n: price }));
  if (def) lines.push(`${t('stat.cooldown')}: ${def.cooldown}s`);
  return { title: tn('crowd', id), sub: def ? t(def.kind === 'harm' ? 'label.crowdHarm' : 'label.crowdHelp') : undefined, body: td('crowd', id), lines, color: def?.color };
}

export function keystoneTip(id: string): TipContent {
  const def = KEYSTONES[id];
  return { title: tn('keystone', id), sub: t('label.keystones'), body: td('keystone', id), tags: def?.synergy ?? [], color: def?.color };
}

export function consumableTip(id: string, n?: number): TipContent {
  const def = CONSUMABLES[id];
  return { title: tn('cons', id) + (n ? ` ×${n}` : ''), sub: t('label.consumables'), body: td('cons', id), color: def?.color };
}

export function itemTip(item: ItemInst): TipContent {
  const def = ITEMS[item.def];
  return {
    title: tn('item', item.def),
    sub: `${t('label.rarity' + item.rarity)} · ${def?.slot === 'weapon' ? t('label.weapon') : def?.slot === 'armor' ? t('label.armor') : t('label.charm')}`,
    body: td('item', item.def),
    lines: item.affixes.map((a) => affixText(a.stat, a.add, a.inc)),
    color: ['#b8b8b8', '#4f9cff', '#c65cff', '#ffb347'][item.rarity],
  };
}

export function Btn(props: { onClick?: () => void; children: ComponentChildren; primary?: boolean; ghost?: boolean; small?: boolean; block?: boolean; disabled?: boolean; danger?: boolean; title?: string }) {
  const cls = ['btn', props.primary && 'primary', props.ghost && 'ghost', props.small && 'small', props.block && 'block', props.danger && 'danger'].filter(Boolean).join(' ');
  return (
    <button class={cls} onClick={props.onClick} disabled={props.disabled} title={props.title}>
      {props.children}
    </button>
  );
}

export type IconKind = 'skill' | 'keystone' | 'item' | 'cons' | 'aspect' | 'blessing' | 'crowd';

export function iconStyle(kind: IconKind, id: string, skill?: string): { color: string; glyph: string } {
  if (kind === 'blessing') {
    const d = BLESSINGS[id];
    return { color: d?.color ?? '#666', glyph: d?.glyph ?? '✦' };
  }
  if (kind === 'crowd') {
    const d = CROWD_EFFECTS[id];
    return { color: d?.color ?? '#666', glyph: d?.glyph ?? '?' };
  }
  if (kind === 'skill') {
    const d = SKILLS[id];
    return { color: d?.color ?? '#666', glyph: d?.glyph ?? '?' };
  }
  if (kind === 'keystone') {
    const d = KEYSTONES[id];
    return { color: d?.color ?? '#666', glyph: d?.glyph ?? '?' };
  }
  if (kind === 'item') {
    const d = ITEMS[id];
    return { color: d?.color ?? '#666', glyph: d?.glyph ?? '?' };
  }
  if (kind === 'cons') {
    const d = CONSUMABLES[id];
    return { color: d?.color ?? '#666', glyph: d?.glyph ?? '?' };
  }
  const parent = skill ? SKILLS[skill] : undefined;
  const asp = skill ? aspectOf(skill, id) : undefined;
  return { color: asp?.color ?? parent?.color ?? '#666', glyph: parent?.glyph ?? '◆' };
}

export function Icon(props: { kind: IconKind; id: string; skill?: string; size?: 'sm' | 'lg'; level?: number; cd?: number; cdText?: string; count?: number; keyLabel?: string; title?: string; tip?: TipContent; onClick?: () => void; dim?: boolean; sel?: boolean }) {
  const st = iconStyle(props.kind, props.id, props.skill);
  const tip = props.tip;
  const ref = useRef<HTMLDivElement>(null);
  // the hovered slot can be consumed or re-rendered without a mouseleave: drop or refresh its tooltip
  useEffect(() => {
    const el = ref.current;
    return () => hideTipOf(el);
  }, []);
  useEffect(() => {
    if (tip && tipOwnedBy(ref.current)) showTip(ref.current!, tip);
  });
  const url = iconUrl(props.kind, props.id, props.skill);
  const hold = useRef<{ timer: number; moved: boolean; fired: number }>({ timer: 0, moved: false, fired: 0 });
  const onDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    e.preventDefault();
    hold.current.moved = false;
    const el = e.currentTarget as HTMLElement;
    if (props.onClick) {
      // a tap acts, a long press explains
      hold.current.timer = window.setTimeout(() => {
        hold.current.moved = true;
        if (tip) showTip(el, tip);
      }, 350);
      hold.current.fired = performance.now();
      props.onClick();
    } else if (tip) {
      showTip(el, tip);
      window.setTimeout(hideTip, 4000);
    }
  };
  const onUp = () => {
    if (hold.current.timer) window.clearTimeout(hold.current.timer);
    hold.current.timer = 0;
  };
  return (
    <div
      ref={ref}
      class={['icon', props.size, (props.onClick || tip) && 'clickable', props.dim && 'dim', url && 'has-img', props.sel && 'sel'].filter(Boolean).join(' ')}
      style={{ background: url ? '#14101c' : st.color }}
      title={tip ? undefined : props.title}
      onClick={props.onClick ? (e) => {
        e.stopPropagation();
        if (performance.now() - hold.current.fired < 700) return;
        props.onClick!();
      } : undefined}
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onMouseEnter={tip && !isTouchUi() ? (e) => showTip(e.currentTarget as HTMLElement, tip) : undefined}
      onMouseLeave={tip && !isTouchUi() ? hideTip : undefined}
    >
      {url ? <img src={url} alt="" draggable={false} /> : st.glyph}
      {props.cd !== undefined && props.cd > 0 && <div class="cd" style={{ transform: `scaleY(${props.cd})` }} />}
      {props.cd !== undefined && props.cd > 0 && props.cdText && <span class="cdtxt">{props.cdText}</span>}
      {props.level !== undefined && <span class="lvl">{props.level}</span>}
      {props.count !== undefined && <span class="count">{props.count}</span>}
      {props.keyLabel && <span class="key">{props.keyLabel}</span>}
    </div>
  );
}

export function EmptySlot(props: { keyLabel?: string; size?: 'sm' | 'lg' }) {
  return (
    <div class={['icon', 'empty', props.size].filter(Boolean).join(' ')}>
      {props.keyLabel && <span class="key">{props.keyLabel}</span>}
    </div>
  );
}

export function itemTitle(item: ItemInst): string {
  const lines = [tn('item', item.def) + ' · ' + t('label.rarity' + item.rarity), td('item', item.def)];
  for (const a of item.affixes) lines.push(affixText(a.stat, a.add, a.inc));
  return lines.join('\n');
}

export function affixText(stat: string, add?: number, inc?: number): string {
  const name = t('stat.' + stat);
  if (add !== undefined) return `${add > 0 ? '+' : ''}${Math.round(add * 100) / 100} ${name}`;
  if (inc !== undefined) return `${inc > 0 ? '+' : ''}${Math.round(inc * 100)}% ${name}`;
  return name;
}

const portraitCache = new Map<string, string>();

export function HeroPortrait(props: { hero: HeroId; size?: number }) {
  const ref = useRef<HTMLImageElement>(null);
  useEffect(() => {
    let src = portraitCache.get(props.hero);
    if (!src) {
      src = heroPortrait(props.hero);
      portraitCache.set(props.hero, src);
    }
    if (ref.current) ref.current.src = src;
  }, [props.hero]);
  const s = props.size ?? 64;
  return <img ref={ref} alt="" style={{ width: s, height: s, objectFit: 'contain' }} />;
}

export const AVATARS = ['⚔', '🛡', '🏹', '🔮', '☠', '🕯', '🐺', '🦇', '🌙', '🔥', '❄', '⚡'];

export function Avatar(props: { i: number; lg?: boolean; sel?: boolean; onClick?: () => void }) {
  return (
    <div class={['avatar', props.lg && 'lg', props.sel && 'sel', props.onClick && 'clickable'].filter(Boolean).join(' ')} onClick={props.onClick}>
      {AVATARS[props.i % AVATARS.length]}
    </div>
  );
}

export function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  ta.remove();
  return Promise.resolve();
}

export function origin(): string {
  return location.origin + location.pathname;
}

export function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return m > 0 ? t('label.min', { m, s }) : t('label.sec', { n: s });
}
